/**
 * gstUtils.ts — Desktop subset of BigxBook gstUtils.
 *
 * Only the pure helpers used by billingEngine are ported. The async
 * DB-backed variants (loadGSTRateSlabsFromDB, INDIAN_STATES helpers,
 * amountInWords) are intentionally excluded from v1 desktop billing.
 *
 * All monetary arithmetic is in integer "paisa" (×100).
 */

// ═══════════════════════════════════════════════════════
//  GST RATE SLABS
// ═══════════════════════════════════════════════════════

/** Standard Indian GST rates (% of taxable value). */
export const VALID_GST_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28] as const;

export type ValidGSTRate = (typeof VALID_GST_RATES)[number];

/** Returns true if the supplied rate matches any standard GST slab. */
export function isValidGSTRate(rate: number): boolean {
    return (VALID_GST_RATES as readonly number[]).includes(rate);
}

// ═══════════════════════════════════════════════════════
//  ROUNDING
// ═══════════════════════════════════════════════════════

export type RoundingMode = 'NEAREST' | 'UP' | 'DOWN' | 'NONE';

/**
 * Applies rounding to a paisa amount (integer) to nearest rupee boundary.
 * Returns the final rounded paisa value.
 */
export function applyRounding(paisaAmount: number, mode: RoundingMode): number {
    switch (mode) {
        case 'NEAREST':
            return Math.round(paisaAmount / 100) * 100;
        case 'UP':
            return Math.ceil(paisaAmount / 100) * 100;
        case 'DOWN':
            return Math.floor(paisaAmount / 100) * 100;
        case 'NONE':
            return paisaAmount;
        default:
            return Math.round(paisaAmount / 100) * 100;
    }
}

// ═══════════════════════════════════════════════════════
//  TAX-INCLUSIVE REVERSE CALCULATION
// ═══════════════════════════════════════════════════════

/**
 * Reverse-calculates taxable amount from a tax-inclusive price.
 *
 * Formula: taxable = inclusiveAmount / (1 + gstRate/100 + cessRate/100)
 *
 * All arithmetic in paisa (integer).
 */
export function reverseCalculateTaxable(
    inclusiveAmountPaisa: number,
    gstRate: number,
    cessRate: number,
): number {
    const totalRateMultiplied = 10000 + (gstRate + cessRate) * 100;
    return Math.round((inclusiveAmountPaisa * 10000) / totalRateMultiplied);
}
